import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  ContentCut as ContentCutIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Search as SearchIcon,
  EmojiEmotions as EmojiEmotionsIcon,
} from '@mui/icons-material';
import { useAdminContext } from '../AdminContext';
import { CDN_BASE_URL } from '../../../config';

export default function ImagesTab() {
  const {
    subreddit,
    flairSheetCdnSub,
    selectedImages,
    setSelectedImages,
    isUploadingImages,
    handleImageUpload,
    uploadProgress,
    handleListUnusedImages,
    isLoadingUnusedImages,
    isPurgingImages,
    handleListMissingImages,
    isLoadingMissingImages,
    showMissingImagesDialog,
    setShowMissingImagesDialog,
    missingImages,
    showPurgeDialog,
    setShowPurgeDialog,
    unusedImages,
    handlePurgeUnusedImages,
    redditEmojiAudit,
    showRedditEmojiPurgeDialog,
    setShowRedditEmojiPurgeDialog,
    isLoadingRedditEmojiAudit,
    isPurgingRedditEmojiOrphans,
    handleRedditEmojiAudit,
    handlePurgeRedditEmojiOrphans,
    forceEmojiImageNames,
    setForceEmojiImageNames,
    handleForceEmojiUpdate,
    isPushingForceEmojis,
    cropToSidesOptions,
    cropToSidesSelected,
    cropToSidesCdnStem,
    cropToSidesPreviewBust,
    isLoadingCropToSidesOptions,
    isResolvingCropToSidesStem,
    isCroppingToSides,
    handleLoadCropToSidesOptions,
    handleCropToSidesSelectionChange,
    handleCropImageToSidesConfirm,
  } = useAdminContext();

  const cdnSubForPaths = flairSheetCdnSub || subreddit || '';

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Import Images
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload images to CDN. Images will be uploaded to the orig folder and resized to all standard sizes.
          <br />
          <strong>Note:</strong> If an image with the same name already exists, it will be overwritten.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="image-upload-input"
            type="file"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              setSelectedImages(files);
            }}
          />
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="folder-upload-input"
            type="file"
            {...({ webkitdirectory: '', directory: '' } as any)}
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              const imageFiles = files.filter((file) => file.type.startsWith('image/'));
              setSelectedImages(imageFiles);
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <label htmlFor="image-upload-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={isUploadingImages}>
                Select Images
              </Button>
            </label>
            <label htmlFor="folder-upload-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={isUploadingImages}>
                Select Folder
              </Button>
            </label>
          </Box>
          {selectedImages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Selected {selectedImages.length} image(s):
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                {selectedImages.map((file: File, idx: number) => (
                  <Chip key={idx} label={file.name} sx={{ mr: 1, mb: 1 }} size="small" />
                ))}
              </Box>
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadIcon />}
          onClick={handleImageUpload}
          disabled={selectedImages.length === 0 || isUploadingImages}
        >
          {isUploadingImages ? 'Uploading...' : 'Upload Images'}
        </Button>
        {isUploadingImages && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress.overall || 0} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {uploadProgress.currentBatch && uploadProgress.totalBatches
                ? `Uploading batch ${uploadProgress.currentBatch} of ${uploadProgress.totalBatches}... ${Math.round(uploadProgress.overall || 0)}%`
                : `Uploading... ${Math.round(uploadProgress.overall || 0)}%`}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Purge Unused Images (CDN)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scans S3/CDN and removes files that are not referenced in the <strong>image</strong> table (active or inactive rows both count as registered). Deletes all standard sizes per name. When you confirm, matching custom Reddit emojis are also removed for those CDN-only names. This does <strong>not</strong> use master_sheet — use &quot;Scan Reddit emojis&quot; below to clean emojis against master_sheet.
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<DeleteIcon />}
            onClick={handleListUnusedImages}
            disabled={isLoadingUnusedImages || isPurgingImages}
          >
            {isLoadingUnusedImages ? 'Scanning...' : 'Purge Unused Images'}
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<SearchIcon />}
            onClick={handleListMissingImages}
            disabled={isLoadingMissingImages}
            sx={{ ml: 2 }}
          >
            {isLoadingMissingImages ? 'Scanning...' : 'Show Missing Images'}
          </Button>
        </Box>
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Reddit emojis vs master sheet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lists custom emojis on the subreddit and compares names to <strong>master_sheet.flair_code</strong> (all rows). Orphans exist on Reddit but have no matching flair_code — different from CDN purge. Removing orphans does <strong>not</strong> delete CDN files or touch the image table, even for inactive images still in the DB.
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<EmojiEmotionsIcon />}
            onClick={handleRedditEmojiAudit}
            disabled={isLoadingRedditEmojiAudit || isPurgingRedditEmojiOrphans}
          >
            {isLoadingRedditEmojiAudit ? 'Scanning Reddit…' : 'Scan Reddit emojis'}
          </Button>
        </Box>
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Force Emoji Update
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Push specific emojis to Reddit by image name. Enter comma-separated image names (e.g. Duke Blue Devils, North Carolina Tar Heels).
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Image names (comma-separated)"
            value={forceEmojiImageNames}
            onChange={(e) => setForceEmojiImageNames(e.target.value)}
            sx={{ mb: 2, maxWidth: 500 }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PublishIcon />}
            onClick={handleForceEmojiUpdate}
            disabled={isPushingForceEmojis || !forceEmojiImageNames.trim()}
          >
            {isPushingForceEmojis ? 'Pushing...' : 'Force Emoji Update'}
          </Button>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Crop Image to Sides
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick a registered image, preview the <code>orig</code> asset (border shows the full canvas), then confirm to
              trim transparent padding so every edge touches a non-transparent pixel. If already tight, no CDN write occurs.
              After a real crop, all standard sizes are re-uploaded and the same Reddit emoji / stylesheet push as Force
              Emoji runs for that image.
            </Typography>
            <Autocomplete
              size="small"
              sx={{ maxWidth: 500, mb: 2 }}
              options={cropToSidesOptions}
              value={cropToSidesSelected}
              onChange={(_, v) => {
                void handleCropToSidesSelectionChange(v);
              }}
              onOpen={() => {
                void handleLoadCropToSidesOptions();
              }}
              loading={isLoadingCropToSidesOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Image name"
                  placeholder="Search or pick from list"
                  helperText={isResolvingCropToSidesStem ? 'Resolving CDN path…' : ' '}
                />
              )}
            />
            {cropToSidesCdnStem && cdnSubForPaths && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'inline-block',
                    lineHeight: 0,
                    width: 'fit-content',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    border: '3px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    backgroundImage: `
                      linear-gradient(45deg, #c9c9c9 25%, transparent 25%),
                      linear-gradient(-45deg, #c9c9c9 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #c9c9c9 75%),
                      linear-gradient(-45deg, transparent 75%, #c9c9c9 75%)`,
                    backgroundSize: '16px 16px',
                    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                  }}
                >
                  <Box
                    component="img"
                    alt={`orig ${cropToSidesCdnStem}`}
                    src={`${CDN_BASE_URL}/orig/${cdnSubForPaths}/${encodeURIComponent(cropToSidesCdnStem)}.png?bust=${cropToSidesPreviewBust}`}
                    sx={{
                      display: 'block',
                      margin: 0,
                      padding: 0,
                      verticalAlign: 'top',
                      width: 'auto',
                      height: 'auto',
                      maxHeight: 360,
                      maxWidth: '100%',
                      imageRendering: 'auto',
                    }}
                  />
                </Box>
              </Box>
            )}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ContentCutIcon />}
              onClick={() => void handleCropImageToSidesConfirm()}
              disabled={
                isCroppingToSides ||
                isPushingForceEmojis ||
                !cropToSidesSelected ||
                !cropToSidesCdnStem ||
                isResolvingCropToSidesStem
              }
            >
              {isCroppingToSides ? 'Cropping & pushing…' : 'Confirm crop to sides'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={showMissingImagesDialog} onClose={() => setShowMissingImagesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Missing Images ({missingImages.length})</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            These images are registered in the database but don't exist in the CDN. Upload them to fix this.
          </Alert>
          {missingImages.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {missingImages.map((imageName: string, idx: number) => (
                <Chip key={idx} label={imageName} sx={{ mr: 1, mb: 1 }} size="small" />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No missing images found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingImagesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRedditEmojiPurgeDialog}
        onClose={() => !isPurgingRedditEmojiOrphans && setShowRedditEmojiPurgeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Remove orphan Reddit emojis</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These custom emojis exist on /r/{subreddit || ''} but have no matching{' '}
            <code>flair_code</code> in master_sheet. Confirm to delete them from Reddit only (CDN unchanged).
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Orphans ({redditEmojiAudit?.orphans?.length ?? 0}):
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {(redditEmojiAudit?.orphans ?? []).map((name: string, idx: number) => (
              <Chip key={idx} label={name} sx={{ mr: 1, mb: 1 }} size="small" />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Matched: {redditEmojiAudit?.matched_count ?? 0} · On Reddit: {redditEmojiAudit?.count_on_reddit ?? 0} · Distinct
            flair_code in master_sheet: {redditEmojiAudit?.master_sheet_flair_codes ?? '—'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRedditEmojiPurgeDialog(false)} disabled={isPurgingRedditEmojiOrphans}>
            Cancel
          </Button>
          <Button
            onClick={handlePurgeRedditEmojiOrphans}
            color="error"
            variant="contained"
            disabled={isPurgingRedditEmojiOrphans || !(redditEmojiAudit?.orphans && redditEmojiAudit.orphans.length > 0)}
          >
            {isPurgingRedditEmojiOrphans ? 'Removing…' : 'Remove from Reddit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showPurgeDialog} onClose={() => !isPurgingImages && setShowPurgeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm Purge Unused Images (CDN)</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete {unusedImages.length} image(s) and all their sizes from the CDN. This action cannot be undone. Reddit emojis with the same names may also be removed.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Images to be deleted ({unusedImages.length}):
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {unusedImages.map((imageName: string, idx: number) => (
              <Chip key={idx} label={imageName} sx={{ mr: 1, mb: 1 }} size="small" />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Each image will be deleted in all sizes: orig, 60x40, 60x60, 128x128, 300x300, 30x30, 300x200
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPurgeDialog(false)} disabled={isPurgingImages}>
            Cancel
          </Button>
          <Button onClick={handlePurgeUnusedImages} color="error" variant="contained" disabled={isPurgingImages}>
            {isPurgingImages ? 'Purging...' : 'Confirm Purge'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
